/**
 * ZIYA global interactions — ports every non-cart behaviour from the
 * original ziya-shapewear.jsx: mobile nav, sticky header state, scroll
 * reveal (useReveal.jsx), product gallery + variant selection, accordions,
 * shop quick-add, results filters, cookie consent, newsletter popup and
 * the chatbot widget.
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------- storage helpers ---------------------------- */
  function readJSON(key) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
  function writeJSON(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) { /* best-effort persistence */ }
  }

  /* ------------------------------ mobile nav ------------------------------- */
  function initMobileNav() {
    var nav = document.querySelector('[data-mobile-nav]');
    if (!nav) return;
    document.querySelectorAll('[data-mobile-nav-open]').forEach(function (btn) {
      btn.addEventListener('click', function () { nav.classList.add('is-open'); });
    });
    document.querySelectorAll('[data-mobile-nav-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { nav.classList.remove('is-open'); });
    });
  }

  /* ---------------------------- sticky header state ------------------------ */
  function initHeaderScroll() {
    var header = document.querySelector('[data-site-header]');
    if (!header) return;
    var onHero = header.getAttribute('data-on-hero') === 'true';
    function update() {
      var scrolled = window.scrollY > 24;
      header.classList.toggle('is-scrolled', scrolled);
      header.classList.toggle('on-hero', onHero && !scrolled);
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ------------------------------ scroll reveal ----------------------------- */
  function initReveal() {
    var els = document.querySelectorAll('[data-reveal]');
    els.forEach(function (el) { el.classList.add('reveal'); });
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    els.forEach(function (el) { observer.observe(el); });
  }

  /* -------------------------------- accordion ------------------------------- */
  function initAccordions() {
    document.querySelectorAll('[data-accordion-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('[data-accordion-item]');
        var panel = item.querySelector('[data-accordion-panel]');
        var isOpen = item.classList.contains('is-open');
        // Only one open at a time within the same accordion list, matching
        // the original's single `open` index state.
        var list = item.parentElement;
        list.querySelectorAll('[data-accordion-item].is-open').forEach(function (openItem) {
          openItem.classList.remove('is-open');
          openItem.querySelector('[data-accordion-panel]').hidden = true;
        });
        if (!isOpen) {
          item.classList.add('is-open');
          panel.hidden = false;
        }
      });
    });
  }

  /* ------------------------------ product gallery ---------------------------- */
  // .gallery-thumbs / .gallery-main-col / .gallery-mobile-wrap are the real
  // grid children (required for the desktop 3-column layout); each one
  // separately nests a nearly-identical [data-gallery-for-color] block per
  // colour. So "the active colour's gallery" is really up to three sibling
  // elements sharing the same data-gallery-for-color value, not one — every
  // helper below operates on all of them together by colour value, never on
  // a single DOM subtree.
  function initProductGallery(root) {
    function colorScopes(colorValue) {
      return root.querySelectorAll('[data-gallery-for-color="' + CSS.escape(colorValue) + '"]');
    }

    function showColorGallery(colorValue) {
      root.querySelectorAll('[data-gallery-for-color]').forEach(function (block) {
        block.hidden = block.getAttribute('data-gallery-for-color') !== colorValue;
      });
    }

    function selectImageIndex(colorValue, index) {
      colorScopes(colorValue).forEach(function (scope) {
        scope.querySelectorAll('[data-gallery-thumb]').forEach(function (t) {
          t.classList.toggle('is-active', t.getAttribute('data-index') === String(index));
        });
        scope.querySelectorAll('[data-gallery-mobile-thumb]').forEach(function (t) {
          t.classList.toggle('is-active', t.getAttribute('data-index') === String(index));
        });
        scope.querySelectorAll('[data-gallery-main-image]').forEach(function (img) {
          img.hidden = img.getAttribute('data-index') !== String(index);
        });
        var dots = scope.querySelectorAll('.gallery-dot');
        dots.forEach(function (dot, i) { dot.classList.toggle('is-active', i === index); });
      });
    }

    root.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-gallery-thumb]');
      if (thumb) {
        var block = thumb.closest('[data-gallery-for-color]');
        var colorValue = block ? block.getAttribute('data-gallery-for-color') : null;
        selectImageIndex(colorValue, parseInt(thumb.getAttribute('data-index'), 10));
      }
      var mobileThumb = e.target.closest('[data-gallery-mobile-thumb]');
      if (mobileThumb) {
        var mBlock = mobileThumb.closest('[data-gallery-for-color]');
        var mColorValue = mBlock ? mBlock.getAttribute('data-gallery-for-color') : null;
        var index = parseInt(mobileThumb.getAttribute('data-index'), 10);
        var scroller = mBlock ? mBlock.querySelector('[data-gallery-scroller]') : null;
        if (scroller) {
          var itemWidth = scroller.clientWidth;
          scroller.scrollTo({ left: index * itemWidth, behavior: 'smooth' });
        }
        selectImageIndex(mColorValue, index);
      }
    });

    root.querySelectorAll('[data-gallery-zoom]').forEach(function (zoom) {
      zoom.addEventListener('mousemove', function (e) {
        var rect = zoom.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        var img = zoom.querySelector('[data-gallery-main-image]:not([hidden]) .gallery-img-zoom');
        if (img) img.style.transformOrigin = x + '% ' + y + '%';
      });
    });

    root.querySelectorAll('[data-gallery-scroller]').forEach(function (scroller) {
      scroller.addEventListener('scroll', function () {
        if (scroller.clientWidth === 0) return;
        var index = Math.round(scroller.scrollLeft / scroller.clientWidth);
        var block = scroller.closest('[data-gallery-for-color]');
        var colorValue = block ? block.getAttribute('data-gallery-for-color') : null;
        selectImageIndex(colorValue, index);
      });
    });

    return { showColorGallery: showColorGallery, selectImageIndex: selectImageIndex, colorScopes: colorScopes };
  }

  /* ------------------------------ variant selection --------------------------- */
  function formatMoney(cents) {
    // Falls back to a plain amount if Shopify.formatMoney/currency isn't
    // available; the price shown right after a swatch/size change is
    // re-derived from the variant JSON's already-formatted price string
    // where possible, so this is only a light fallback.
    return (cents / 100).toFixed(2);
  }

  function initProductForm() {
    var root = document.querySelector('[data-product-root]');
    if (!root) return;

    var productJsonEl = root.querySelector('[data-product-json]');
    if (!productJsonEl) return;
    var product = JSON.parse(productJsonEl.textContent);

    var gallery = initProductGallery(root);
    var variantInput = root.querySelector('[data-selected-variant-id]');
    var priceEl = root.querySelector('[data-purchase-price]');
    var colorLabelEl = root.querySelector('[data-selected-color-label]');
    var sizeLabelEl = root.querySelector('[data-selected-size-label]');
    var addButton = root.querySelector('[data-add-to-cart-button]');
    var addLabels = document.querySelectorAll('[data-add-to-cart-label]');
    var stickyName = document.querySelector('[data-sticky-name]');
    var stickyPrice = document.querySelector('[data-sticky-price]');
    var stickyButtons = document.querySelectorAll('.sticky-add-bar [type="submit"]');

    var colorIndex = -1;
    var sizeIndex = -1;
    (product.options || []).forEach(function (name, i) {
      var down = name.toLowerCase();
      if (down.indexOf('colour') > -1 || down.indexOf('color') > -1 || down.indexOf('kleur') > -1) {
        colorIndex = i;
      } else if (sizeIndex === -1) {
        sizeIndex = i;
      }
    });

    var initialVariantId = variantInput ? Number(variantInput.value) : null;
    var selected = {};
    var current = product.variants.find(function (v) { return v.id === initialVariantId; }) || product.variants[0];
    if (colorIndex > -1) selected[colorIndex] = current.options[colorIndex];
    if (sizeIndex > -1) selected[sizeIndex] = current.options[sizeIndex];
    var lastColor = colorIndex > -1 ? selected[colorIndex] : null;

    function findVariant() {
      return product.variants.find(function (v) {
        return Object.keys(selected).every(function (idx) { return v.options[idx] === selected[idx]; });
      });
    }

    function moneyString(amountCents) {
      // Reuse the server-rendered price string's currency formatting by
      // swapping only the digits, so symbol/decimal/thousands stay correct
      // for the shop's locale without needing Shopify.formatMoney here.
      if (!priceEl) return formatMoney(amountCents);
      var template = priceEl.getAttribute('data-money-format') || priceEl.textContent;
      var digits = formatMoney(amountCents).replace('.', ',');
      return template.replace(/[\d.,]+/, digits);
    }

    function render() {
      var variant = findVariant();
      if (!variant) return;

      if (variantInput) variantInput.value = variant.id;
      if (priceEl) {
        if (!priceEl.getAttribute('data-money-format')) priceEl.setAttribute('data-money-format', priceEl.textContent);
        priceEl.textContent = moneyString(variant.price);
      }
      if (colorIndex > -1 && colorLabelEl) colorLabelEl.textContent = selected[colorIndex];
      if (sizeIndex > -1 && sizeLabelEl) sizeLabelEl.textContent = selected[sizeIndex];

      var available = !!variant.available;
      addLabels.forEach(function (label) {
        label.textContent = available ? label.getAttribute('data-in-stock-text') || label.textContent : label.getAttribute('data-sold-out-text') || label.textContent;
      });
      if (addButton) addButton.disabled = !available;
      stickyButtons.forEach(function (btn) { btn.disabled = !available; });

      if (stickyName) {
        stickyName.textContent = product.title + (colorIndex > -1 ? ' — ' + selected[colorIndex] : '');
      }
      if (stickyPrice) stickyPrice.textContent = moneyString(variant.price);

      // swatch / chip active states + per-combination sold-out state.
      // Colour swatches stay clickable regardless of stock — they only
      // switch which photos/variant are shown, matching the original
      // design where colour is always browsable. Only size chips reflect
      // per-combination availability, since "Add to Bag" already covers
      // the actual stock gate.
      root.querySelectorAll('[data-option-select]').forEach(function (btn) {
        var idx = parseInt(btn.getAttribute('data-option-index'), 10);
        var value = btn.getAttribute('data-option-value');
        var isSelected = selected[idx] === value;
        btn.classList.toggle('is-selected', isSelected);
        var isSwatch = btn.classList.contains('swatch');
        if (isSwatch) btn.setAttribute('aria-pressed', String(isSelected));

        if (isSwatch) {
          btn.classList.remove('is-soldout');
          btn.disabled = false;
          return;
        }

        var hypothetical = Object.assign({}, selected);
        hypothetical[idx] = value;
        var matchingAny = product.variants.some(function (v) {
          return Object.keys(hypothetical).every(function (i) { return v.options[i] === hypothetical[i]; }) && v.available;
        });
        btn.classList.toggle('is-soldout', !matchingAny);
        btn.disabled = !matchingAny;
      });

      if (colorIndex > -1) {
        gallery.showColorGallery(selected[colorIndex]);
        if (selected[colorIndex] !== lastColor) {
          lastColor = selected[colorIndex];
          gallery.selectImageIndex(selected[colorIndex], 0);
          gallery.colorScopes(selected[colorIndex]).forEach(function (scope) {
            var activeScroller = scope.querySelector('[data-gallery-scroller]');
            if (activeScroller) activeScroller.scrollTo({ left: 0 });
          });
        }

        document.querySelectorAll('[data-result-teaser-for-color]').forEach(function (block) {
          block.hidden = block.getAttribute('data-result-teaser-for-color') !== selected[colorIndex];
        });
      }
    }

    root.addEventListener('click', function (e) {
      var opt = e.target.closest('[data-option-select]');
      if (!opt || opt.disabled) return;
      var idx = parseInt(opt.getAttribute('data-option-index'), 10);
      selected[idx] = opt.getAttribute('data-option-value');
      render();
    });

    render();
  }

  /* -------------------------------- shop quick add ----------------------------- */
  function initQuickAdd() {
    document.querySelectorAll('[data-quick-add-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('.shop-two-card');
        card.querySelector('[data-quick-add-panel]').hidden = false;
        card.querySelector('[data-quick-add-default-actions]').hidden = true;
      });
    });
    document.querySelectorAll('[data-quick-add-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('.shop-two-card');
        card.querySelector('[data-quick-add-panel]').hidden = true;
        card.querySelector('[data-quick-add-default-actions]').hidden = false;
      });
    });
    document.querySelectorAll('[data-quick-add-size]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        if (chip.disabled) return;
        var panel = chip.closest('[data-quick-add-panel]');
        panel.querySelectorAll('[data-quick-add-size]').forEach(function (c) { c.classList.remove('is-selected'); });
        chip.classList.add('is-selected');
        var confirmBtn = panel.querySelector('[data-quick-add-confirm]');
        confirmBtn.disabled = false;
        confirmBtn.setAttribute('data-variant-id', chip.getAttribute('data-variant-id'));
      });
    });
    document.querySelectorAll('[data-quick-add-confirm]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId || !window.ZiyaCart) return;
        window.ZiyaCart.addVariant(variantId, 1);
      });
    });
  }

  /* ------------------------------- results filters ------------------------------ */
  function initResultsFilters() {
    var filterBar = document.querySelector('[data-results-filters]');
    if (!filterBar) return;
    filterBar.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-filter-chip]');
      if (!chip) return;
      filterBar.querySelectorAll('[data-filter-chip]').forEach(function (c) { c.classList.remove('is-active'); });
      chip.classList.add('is-active');
      var value = chip.getAttribute('data-filter-value');
      document.querySelectorAll('[data-result-card]').forEach(function (card) {
        var show = value === 'All' || card.getAttribute('data-result-size') === value;
        card.hidden = !show;
      });
    });
  }

  /* ------------------------------- cookie consent -------------------------------- */
  var COOKIE_KEY = 'ziya-cookie-consent';
  var DEFAULT_CONSENT = { necessary: true, preferences: false, analytics: false, marketing: false };

  function initCookieBanner() {
    var banner = document.querySelector('[data-cookie-banner]');
    if (!banner) return;
    var consent = readJSON(COOKIE_KEY);
    if (consent) return; // visitor already chose — stays hidden

    banner.hidden = false;
    var simple = banner.querySelector('[data-cookie-banner-simple]');
    var manage = banner.querySelector('[data-cookie-banner-manage]');

    function save(value) {
      writeJSON(COOKIE_KEY, value);
      banner.hidden = true;
    }

    banner.querySelector('[data-cookie-accept]').addEventListener('click', function () {
      save({ necessary: true, preferences: true, analytics: true, marketing: true });
    });
    banner.querySelector('[data-cookie-reject]').addEventListener('click', function () {
      save(Object.assign({}, DEFAULT_CONSENT));
    });
    banner.querySelector('[data-cookie-manage]').addEventListener('click', function () {
      simple.hidden = true;
      manage.hidden = false;
    });
    banner.querySelector('[data-cookie-back]').addEventListener('click', function () {
      manage.hidden = true;
      simple.hidden = false;
    });
    banner.querySelector('[data-cookie-save-selection]').addEventListener('click', function () {
      var value = Object.assign({}, DEFAULT_CONSENT);
      manage.querySelectorAll('[data-cookie-cat]').forEach(function (input) {
        value[input.getAttribute('data-cookie-cat')] = input.checked;
      });
      save(value);
    });
  }

  function initCookieSettingsPage() {
    var page = document.querySelector('[data-cookie-settings-page]');
    if (!page) return;
    var consent = readJSON(COOKIE_KEY) || DEFAULT_CONSENT;
    page.querySelectorAll('[data-cookie-cat]').forEach(function (input) {
      input.checked = !!consent[input.getAttribute('data-cookie-cat')];
    });
    page.querySelector('[data-cookie-settings-save]').addEventListener('click', function () {
      var value = Object.assign({}, DEFAULT_CONSENT);
      page.querySelectorAll('[data-cookie-cat]').forEach(function (input) {
        value[input.getAttribute('data-cookie-cat')] = input.checked;
      });
      writeJSON(COOKIE_KEY, value);
      page.querySelector('[data-cookie-settings-saved]').hidden = false;
    });
  }

  /* ------------------------------- newsletter popup -------------------------------- */
  // No discount is offered or displayed here — this popup exists purely to
  // grow the real Shopify customer/marketing list (see newsletter-popup.liquid
  // for the {% form 'customer' %} that actually creates the Customer record).
  //
  // Storage keys are versioned ("-insider-v2"): the old 10%-discount popup
  // used plain 'ziya-newsletter' / 'ziya-newsletter-dismissed' with no
  // expiry, so anyone who had already dismissed or claimed THAT popup would
  // have that state permanently block this one from ever showing, even
  // though it's a different offer. Bump this suffix again if the popup's
  // offer changes materially in the future for the same reason.
  var NEWSLETTER_KEY = 'ziya-newsletter-insider-v2';
  var NEWSLETTER_DISMISSED_KEY = 'ziya-newsletter-insider-v2-dismissed';
  var DISMISS_SUPPRESS_DAYS = 7; // closed without signing up — ask again later
  var SIGNUP_SUPPRESS_DAYS = 365; // already signed up — don't re-pester
  var DAY_MS = 24 * 60 * 60 * 1000;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function pushAnalyticsEvent(name) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name });
    } catch (e) { /* analytics must never break the popup itself */ }
  }

  var NEWSLETTER_SUBMIT_TIMEOUT_MS = 10000; // safety net if the hidden iframe never fires 'load'

  function initNewsletterPopup() {
    var popup = document.querySelector('[data-newsletter-popup]');
    if (!popup) return;
    var form = popup.closest('form') || popup.querySelector('form');
    var iframe = form ? form.querySelector('[data-newsletter-iframe]') : null;
    var i18nEl = document.querySelector('[data-newsletter-i18n]');
    var i18n = i18nEl ? JSON.parse(i18nEl.textContent) : {};
    var formState = popup.querySelector('[data-newsletter-form-state]');
    var successState = popup.querySelector('[data-newsletter-success-state]');
    var submitBtn = popup.querySelector('[data-newsletter-submit]');
    var errorEl = popup.querySelector('[data-newsletter-error]');

    // No-JS / no-iframe fallback: Liquid's form.posted_successfully? on the
    // page Shopify redirects back to after a real top-level submission (see
    // newsletter-popup.liquid). With JS enabled this path is no longer
    // normally reached, since submissions target the hidden iframe below
    // instead — but it's kept so the popup still degrades correctly without
    // JavaScript rather than breaking.
    if (i18n.justSubscribed) {
      writeJSON(NEWSLETTER_KEY, { claimedAt: Date.now() });
      pushAnalyticsEvent('newsletter_popup_success');
    }

    var claimed = readJSON(NEWSLETTER_KEY);
    var dismissed = readJSON(NEWSLETTER_DISMISSED_KEY);
    var claimedActive = !!claimed && (Date.now() - claimed.claimedAt) < SIGNUP_SUPPRESS_DAYS * DAY_MS;
    var dismissedActive = !!dismissed && (Date.now() - dismissed.dismissedAt) < DISMISS_SUPPRESS_DAYS * DAY_MS;

    // Priority: an active claimed state always wins, regardless of any
    // dismissed record that might also exist.
    if (!claimedActive && !dismissedActive) {
      // Subtle e-commerce timing: long enough that it doesn't feel like an
      // ambush on arrival, short enough to still catch a first-time visitor
      // before they leave. Suppression is already decided above, before
      // this is ever scheduled, so there's no flash of the popup first.
      window.setTimeout(function () {
        popup.hidden = false;
        pushAnalyticsEvent('newsletter_popup_view');
      }, 4000);
    }

    function close() {
      popup.hidden = true;
      // Only a real close of the FORM (not the post-signup success view)
      // counts as "dismissed without signing up".
      if (formState.hidden === false) writeJSON(NEWSLETTER_DISMISSED_KEY, { dismissedAt: Date.now() });
    }
    popup.querySelector('[data-newsletter-close]').addEventListener('click', close);
    popup.addEventListener('click', function (e) { if (e.target === popup) close(); });

    // Submit the real, CAPTCHA-verified request into the hidden iframe
    // instead of the top-level window, so a genuine native form submission
    // (required for Shopify's own automatic hCaptcha handling — see the
    // file-level comment in newsletter-popup.liquid) never navigates the
    // visitor away from the page they're on.
    if (form && iframe) {
      form.target = 'newsletter-popup-target';
    }

    var submissionInFlight = false;
    var submissionTimer = null;

    function setSubmitting(isSubmitting) {
      if (!submitBtn) return;
      submitBtn.disabled = isSubmitting;
      submitBtn.textContent = isSubmitting ? i18n.submitting : i18n.submitLabel;
    }

    function finishSubmission(success) {
      if (!submissionInFlight) return; // already handled (or never started)
      submissionInFlight = false;
      if (submissionTimer) { window.clearTimeout(submissionTimer); submissionTimer = null; }
      setSubmitting(false);

      if (success) {
        errorEl.hidden = true;
        formState.hidden = true;
        successState.hidden = false;
        successState.querySelector('[data-newsletter-success-title]').textContent = i18n.successTitle;
        successState.querySelector('[data-newsletter-success-text]').textContent = i18n.successText;
        writeJSON(NEWSLETTER_KEY, { claimedAt: Date.now() });
        pushAnalyticsEvent('newsletter_popup_success');
        // Premium touch: close smoothly on its own after the visitor has had
        // a moment to read the confirmation — they never leave the page.
        window.setTimeout(function () { popup.hidden = true; }, 2500);
      } else {
        errorEl.textContent = i18n.submitError;
        errorEl.hidden = false;
      }
    }

    if (iframe) {
      iframe.addEventListener('load', function () {
        if (!submissionInFlight) return; // the iframe's initial blank load, not a real submission
        var success = false;
        try {
          // Shopify appends this exact query param on a successful native
          // customer-form submission (confirmed against this store's real
          // redirect behavior).
          success = /[?&]customer_posted=true(&|#|$)/.test(iframe.contentWindow.location.href);
        } catch (e) {
          // Cross-origin or otherwise inaccessible — can't confirm success.
          success = false;
        }
        finishSubmission(success);
      });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        var input = popup.querySelector('[data-newsletter-email]');
        var value = (input.value || '').trim();

        if (!EMAIL_RE.test(value)) {
          e.preventDefault();
          errorEl.textContent = i18n.invalidEmail;
          errorEl.hidden = false;
          return;
        }
        errorEl.hidden = true;

        if (claimed) {
          // Already on the list from a previous session — no need to
          // submit again; just reassure them.
          e.preventDefault();
          formState.hidden = true;
          successState.hidden = false;
          successState.querySelector('[data-newsletter-success-title]').textContent = i18n.alreadyTitle;
          successState.querySelector('[data-newsletter-success-text]').textContent = i18n.alreadyText;
          return;
        }

        // Deliberately NOT calling e.preventDefault() here: this must stay a
        // genuine, native submit event for Shopify's own hCaptcha handling
        // to engage (see the file-level comment in newsletter-popup.liquid).
        // The only thing redirecting it away from a visible top-level
        // navigation is form.target, set above.
        if (!iframe) {
          // No iframe available for some reason — fall back to a normal
          // full navigation rather than leaving the visitor stuck. Signups
          // still work; they just lose the same-page UX this once.
          pushAnalyticsEvent('newsletter_popup_submit');
          return;
        }

        submissionInFlight = true;
        setSubmitting(true);
        pushAnalyticsEvent('newsletter_popup_submit');
        submissionTimer = window.setTimeout(function () { finishSubmission(false); }, NEWSLETTER_SUBMIT_TIMEOUT_MS);
      });
    }
  }

  /* ---------------------------------- chatbot ---------------------------------- */
  function initChatbot() {
    var bubble = document.querySelector('[data-chatbot-open]');
    var panel = document.querySelector('[data-chatbot-panel]');
    if (!bubble || !panel) return;

    bubble.addEventListener('click', function () { panel.hidden = !panel.hidden; });
    panel.querySelector('[data-chatbot-close]').addEventListener('click', function () { panel.hidden = true; });

    var messagesEl = panel.querySelector('[data-chatbot-messages]');
    var topicsEl = panel.querySelector('[data-chatbot-topics]');

    function addMessage(text, from) {
      var p = document.createElement('p');
      p.className = 'chat-msg chat-msg-' + from;
      p.textContent = text;
      messagesEl.appendChild(p);
    }

    topicsEl.addEventListener('click', function (e) {
      var topic = e.target.closest('[data-chatbot-topic]');
      if (!topic) return;
      addMessage(topic.textContent, 'user');
      addMessage(topic.getAttribute('data-answer'), 'bot');
      topicsEl.hidden = true;
    });

    var input = panel.querySelector('[data-chatbot-input]');
    var fallbackEl = document.querySelector('[data-chatbot-fallback-answer]');
    var fallbackAnswer = fallbackEl ? JSON.parse(fallbackEl.textContent) : '';

    function send() {
      var value = (input.value || '').trim();
      if (!value) return;
      addMessage(value, 'user');
      addMessage(fallbackAnswer, 'bot');
      input.value = '';
      topicsEl.hidden = true;
    }
    panel.querySelector('[data-chatbot-send]').addEventListener('click', send);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') send(); });
  }

  /* ------------------------------------ init ------------------------------------ */
  initMobileNav();
  initHeaderScroll();
  initReveal();
  initAccordions();
  initProductForm();
  initQuickAdd();
  initResultsFilters();
  initCookieBanner();
  initCookieSettingsPage();
  initNewsletterPopup();
  initChatbot();
})();
